import Link from 'next/link';
import React, { HTMLAttributes, JSX } from 'react';
import { twMerge } from 'tailwind-merge';

import { getAccessibleFocusStyles } from '@videoblocks/design-system';

export interface BaseLinkWrapperProps {
    children: React.ReactNode;
    link?: string;
    className?: string;
    style?: React.CSSProperties;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    ariaLabelledBy?: string;
    role?: string;
    tabIndex?: number;
    onKeyDown?: React.KeyboardEventHandler;
    showExternalIcon?: boolean;
    externalLinkText?: string;
}

type LinkWrapperProps<T = object> = BaseLinkWrapperProps & T & React.HTMLAttributes<HTMLElement>;

const ExternalLinkIcon = () => (
    <svg
        className="inline-block w-4 h-4 ml-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
    </svg>
);

export default function LinkWrapper<T = object>({
    children,
    link,
    className,
    style,
    ariaLabel,
    ariaDescribedBy,
    ariaLabelledBy,
    role,
    tabIndex,
    onKeyDown,
    showExternalIcon = false,
    externalLinkText = 'opens in new window',
    ...additionalProps
}: LinkWrapperProps<T>): JSX.Element {
    if (!link && (className || style || (additionalProps && Object.keys(additionalProps).length > 0))) {
        const divProps: HTMLAttributes<HTMLDivElement> = {
            className,
            style,
            role,
            tabIndex,
            onKeyDown,
            ...additionalProps,
        };

        if (ariaLabel) divProps['aria-label'] = ariaLabel;
        if (ariaDescribedBy) divProps['aria-describedby'] = ariaDescribedBy;
        if (ariaLabelledBy) divProps['aria-labelledby'] = ariaLabelledBy;

        return <div {...divProps}>{children}</div>;
    }

    if (!link) {
        return <>{children}</>;
    }

    const isInternalLink = (): boolean => {
        const url = link;
        if (!url) return false;
        if (url.startsWith('/') || !url.startsWith('http')) return true;

        try {
            const parsed = new URL(url);
            if (typeof window !== 'undefined') {
                return parsed.origin === window.location.origin;
            }
            return parsed.hostname.endsWith('storyblocks.com');
        } catch {
            return true;
        }
    };

    const internal = isInternalLink();

    // Build comprehensive ARIA label for external links
    const buildAriaLabel = () => {
        if (ariaLabel) return ariaLabel;
        if (!internal) {
            const childText = typeof children === 'string' ? children : '';
            return `${childText} ${externalLinkText}`.trim();
        }
        return undefined;
    };

    const linkProps = {
        href: link,
        className: twMerge(
            'no-underline inline-block hover:no-underline rounded-md',
            getAccessibleFocusStyles(),
            className,
        ),
        target: internal ? undefined : '_blank',
        rel: internal ? undefined : 'noopener noreferrer',
        style,
        'aria-label': buildAriaLabel(),
        'aria-describedby': ariaDescribedBy,
        'aria-labelledby': ariaLabelledBy,
        role,
        tabIndex,
        onKeyDown,
        ...additionalProps,
    };

    return (
        <Link {...linkProps}>
            {children}
            {!internal && externalLinkText && (
                <>
                    {showExternalIcon && <ExternalLinkIcon />}
                    <span className="sr-only">{externalLinkText}</span>
                </>
            )}
        </Link>
    );
}
